// ============================================================
// HOOKED — APP SHELL
// Title splash → The Lake. Core keyframes injected once here,
// per the injection law. Angler state lives at the root so the
// zone and the fishing overlay share one reducer.
//
// v2 overhaul: the splash is now a scene — living Atmosphere,
// an orchestrated title reveal, a drifting bobber, and a
// lantern-lit prompt. The scaffolding splash is gone.
//
// v3 — THE LEDGER REMEMBERS. The angler now persists to
// localStorage (the interim lane; Firebase replaces this key
// wholesale later). Saved state merges OVER initialAngler so
// new fields added in future updates default cleanly — no
// migration ceremonies. Delta-write law unchanged: this is a
// snapshot of reducer output, never a hand-edit of state.
// ============================================================

import { useEffect, useReducer, useState } from "react";
import { SD, GM, NIGHT_BG, ensureCoreKeyframes, textGlow, bloom } from "./theme";
import { Atmosphere } from "./Atmosphere";
import { anglerReducer, initialAngler } from "./angler";
// ⚑ THE COMPATIBILITY CONTRACT — Lake3D takes the EXACT same props
// as Lake. To go back to the 2D board, swap which line is commented.
// import { Lake } from "./Lake";              // the 2D board
// import { Lake3D as Lake } from "./Lake3D";  // the moonlit wharf (3D)
import { Wharf3D as Lake } from "./Wharf3D";   // THE WORLD, off the end of the Pier (2026-09-25)

// ── Persistence — the interim ledger (Firebase's seat is saved) ─
const STORAGE_KEY = "hooked_angler_v1";
function loadAngler() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialAngler;
    const saved = JSON.parse(raw);
    return {
      ...initialAngler,
      ...saved,
      tackle:   { ...initialAngler.tackle,   ...(saved.tackle   || {}) },
      wardrobe: { ...initialAngler.wardrobe, ...(saved.wardrobe || {}) },
      unlocked: Array.isArray(saved.unlocked) && saved.unlocked.length
        ? saved.unlocked
        : initialAngler.unlocked,
    };
  } catch {
    return initialAngler;
  }
}

export default function App() {
  const [angler, dispatch] = useReducer(anglerReducer, undefined, loadAngler);
  const [started, setStarted] = useState(false);

  useEffect(() => { ensureCoreKeyframes(); }, []);

  // Save on every reducer beat — a snapshot, never a hand-write.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(angler)); } catch {}
  }, [angler]);

  useEffect(() => {
    if (started) return;
    const onKey = (e) => {
      if (e.code === "Space" || e.key === "Enter") { e.preventDefault(); setStarted(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [started]);

  if (!started) {
    return (
      <div
        onPointerDown={() => setStarted(true)}
        style={{
          position: "fixed", inset: 0, background: NIGHT_BG,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontFamily: GM.fontDisplay, userSelect: "none",
          overflow: "hidden",
        }}>
        <Atmosphere stars={130} motes={26} moonX="50%" moonY="26%" seed={19} />

        {/* the drifting bobber, far below the moon */}
        <div style={{
          position: "absolute", top: "46%", left: "50%",
          fontSize: 26, transform: "translateX(-50%)",
          animation: "bobberIdle 3s ease-in-out infinite",
          filter: `drop-shadow(0 0 10px ${SD.gold}88)`,
          zIndex: 1,
        }}>🎣</div>

        {/* title block */}
        <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <div style={{
            fontSize: "clamp(46px, 12vw, 92px)", fontWeight: 900,
            color: GM.goldBright,
            letterSpacing: 14,
            ...textGlow(GM.gold),
            animation: "titleRise 1.4s cubic-bezier(.2,.7,.2,1) both",
            textShadow: `0 0 8px ${GM.gold}88, 0 0 22px ${GM.gold}44, 0 0 60px ${GM.gold}22, 0 4px 0 rgba(0,0,0,0.5)`,
          }}>
            HOOKED
          </div>
          {/* brass underline that sheens */}
          <div style={{
            height: 2, width: "72%", margin: "16px auto 0",
            background: `linear-gradient(90deg, transparent, ${GM.goldPale}, ${GM.gold}, ${GM.goldPale}, transparent)`,
            backgroundSize: "220% 100%",
            animation: "railSheen 4s linear infinite",
            boxShadow: bloom(GM.gold),
          }} />
          <div style={{
            fontSize: "clamp(9px, 2vw, 11px)", letterSpacing: 6, color: GM.inkDim,
            marginTop: 16, animation: "fadeUp 1.2s ease 0.5s both",
          }}>
            THE WATER WAITS
          </div>
        </div>

        <div style={{
          position: "relative", zIndex: 2,
          fontSize: 10, letterSpacing: 3, color: GM.goldBright, marginTop: 54,
          padding: "8px 20px",
          border: `1px solid ${GM.gold}55`, borderRadius: 999,
          background: `${GM.gold}0c`,
          animation: "lanternFlicker 3.5s ease-in-out infinite",
          boxShadow: `0 0 20px ${GM.gold}22, inset 0 0 12px ${GM.gold}11`,
        }}>
          SPACE / TAP TO BEGIN
        </div>
      </div>
    );
  }

  return <Lake angler={angler} dispatch={dispatch} />;
}
